import { ProductInternalStatus } from './../../../models/product.model';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { BreadcrumbServiceService } from 'src/app/shared/services/breadcrumb-service.service';
import {
  BaseUserModel,
  UserPermissionsModel,
  UserRolesModel,
} from 'src/app/Models/userModel';
import { UserService } from 'src/app/shared/services/user-service';
import {
  PageSizes,
  PaginationUtilsService,
} from 'src/app/shared/services/pagination-utils.service';
import { Subject, distinctUntilChanged, debounceTime } from 'rxjs';
import { UserApiService } from 'src/app/shared/services/user-api-service';
import { AddUpdateProductComponent } from '../add-update-product/add-update-product.component';
import { PaginationComponent } from 'src/app/shared/components/pagination/pagination.component';
import { AiBotComponent } from '../ai-bot/ai-bot.component';
import { ProductDetailComponent } from '../product-detail/product-detail.component';
import {
  IMSproductResponse,
  PaginatedIMSproductsResponse,
  ProductListingStatus,
} from '../../../models/product.model';
import { CustuomtoastserviceService } from 'src/app/shared/services/custuomtoastservice.service';
import { ProductService } from '../../../services/product-api.service';
import { Apps } from 'src/app/shared/enums/enums';
import { NamedResponse } from 'src/app/shared/models/shared.model';
import { ErrorHandlerService } from 'src/app/shared/services/error-handler.service';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-product-list',
  standalone: true,
  imports: [
    CommonModule,
    AddUpdateProductComponent,
    ProductDetailComponent,
    PaginationComponent,
    AiBotComponent,
    MatTooltip
  ],
  templateUrl: './product-list.component.html',
  styleUrl: './product-list.component.css',
})
export class ProductListComponent implements OnInit, OnDestroy {
  //child components
  @ViewChild('productAddEdit') productAddEdit!: AddUpdateProductComponent;
  @ViewChild('productDetails') productDetails!: ProductDetailComponent;

  //user variables
  userPermissions: UserPermissionsModel[] = [];
  userDetails: BaseUserModel = new BaseUserModel();
  //pagination variables
  paginatedProductsList: PaginatedIMSproductsResponse =
    new PaginatedIMSproductsResponse();
  searchSubject = new Subject<string>();
  currentPage: number = 1;
  pageSizes = PageSizes;
  itemsPerPage: number = this.pageSizes[0];
  totalRecords: number = 0;
  totalPages: number = 0;
  pagesToShow: number[] = [];
  //ai-bot flag
  showAiDialog: boolean = false;
  //default warehouse id
  defaultWareHouseId: number = 0;
  //product required Enum List
  productStatusEnumList: NamedResponse[] = [];
  productTypeEnumList: NamedResponse[] = [];
  conditionEnumList: NamedResponse[] = [];
  internalStausEnumList: NamedResponse[] = [];
  discriminatorEnumList: NamedResponse[] = [];
  trackInventoryOptionEnumList: NamedResponse[] = [];
  listingStatusEnumList: NamedResponse[] = [];
  dimensionUnitEnumList: NamedResponse[] = [];
  weightUnitEnumList: NamedResponse[] = [];
  //product-sort variables
  sortField: string = 'CreatedAt';
  sortDirection: 'asc' | 'desc' = 'desc';
  internalStatusIcons = [
    '../../../../../assets/Icons/ai-process.gif',
    '../../../../../assets/Icons/Image_pending.svg',
    '../../../../../assets/Icons/complete-product.svg',
    '../../../../../assets/Icons/product_draft_ic.svg',
  ];
  listingStatusIcons = [
    '../../../../../assets/Icons/product_draft_ic.svg',
    '../../../../../assets/Icons/queued_listing.svg',
    '../../../../../assets/Icons/listing-process.svg',
  ];

  productListingStatus=ProductListingStatus;
  productInternalStatus =ProductInternalStatus
  constructor(
    private breadcrumbService: BreadcrumbServiceService,
    private userService: UserService,
    private userapiservice: UserApiService,
    private productService: ProductService,
    private paginationUtils: PaginationUtilsService,
    private toaster: CustuomtoastserviceService,
    private errorService: ErrorHandlerService
  ) {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm: string) => {
        this.getProductsList(searchTerm);
      });
  }
  private subscription!: Subscription;

  async ngOnInit() {
    let userObj = this.userService.getUserFromLocalStorage();
    if (userObj) this.userDetails = userObj;
    this.defaultWareHouseId = Number(localStorage.getItem('warehouseId'));

    this.breadcrumbService.setBreadcrumbs([
      { label: 'Products', url: 'products' },
    ]);

    this.getUserRolesAndPermissions();
    await Promise.all([this.loadLookupsAsync()]), this.getProductsList();
    // then call every 2 minutes (180000 ms)
    this.subscription = interval(60000).subscribe(() => {
      this.getProductsList();
    });
  }

  ngOnDestroy() {
    // cleanup when component is destroyed
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  async getUserRolesAndPermissions(): Promise<void> {
    try {
      const userId = this.userDetails.userId;
      const data = await this.userapiservice
        .getUserRoleDetails(userId)
        .toPromise();

      const userPermissions: UserPermissionsModel[] = [];

      if (data) {
        const userRoles = data as UserRolesModel[];
        const appRoles = userRoles?.filter(
          (role) => role.appId === Apps.Lister
        );

        appRoles?.forEach((role) => {
          userPermissions.push(...role.permissions);
        });
      }

      this.userPermissions = userPermissions;
    } catch (error) {
      this.userPermissions = [];
    }
  }

  hasPermission(permissionName: string): boolean {
    return this.userPermissions.some(
      (permission) => permission.name === permissionName
    );
  }
  async loadLookupsAsync(): Promise<void> {
    try {
      const storageKey = 'lookups';
      const storedData = await localStorage.getItem(storageKey);

      if (!storedData) return;

      const dataArray = await JSON.parse(storedData);
      if (!Array.isArray(dataArray)) return;

      this.productStatusEnumList = this.getLookupItems(
        dataArray,
        'ProductStatus'
      );
      this.conditionEnumList = this.getLookupItems(
        dataArray,
        'ProductCondition'
      );
      this.productTypeEnumList = this.getLookupItems(dataArray, 'ProductType');
      this.internalStausEnumList = this.getLookupItems(
        dataArray,
        'ProductInternalStatus'
      );
      this.listingStatusEnumList = this.getLookupItems(
        dataArray,
        'ProductListingStatus'
      );
      this.discriminatorEnumList = this.getLookupItems(
        dataArray,
        'ProductDiscriminator'
      );
      this.trackInventoryOptionEnumList = this.getLookupItems(
        dataArray,
        'TrackInventoryOption'
      );
      this.dimensionUnitEnumList = this.getLookupItems(
        dataArray,
        'DimensionUnits'
      );
      this.weightUnitEnumList = this.getLookupItems(dataArray, 'WeightUnits');
    } catch (error) {
      this.toaster.error('Failed to load lookups');
    }
  }
  private getLookupItems(data: any[], key: string): any[] {
    return data.find((item) => item.name === key)?.items || [];
  }

  getProductsList(searchTerm?: string) {
    if (searchTerm) {
      this.currentPage = 1;
    }

    const sortCriteria = `${this.sortField} ${this.sortDirection}`;

    this.productService
      .getProducts(
        this.userDetails.companyId,
        this.defaultWareHouseId,
        this.currentPage,
        this.itemsPerPage,
        searchTerm,
        sortCriteria,
      )
      .subscribe((data: PaginatedIMSproductsResponse) => {
        data.items.forEach((item: IMSproductResponse) => {
          item.disciminatorName =
            item.discriminator > 0
              ? this.discriminatorEnumList[item.discriminator - 1].name
              : '-';
          item.statusName =
            item.status > 0
              ? this.productStatusEnumList[item.status - 1].name
              : '-';
          item.listerProduct.conditionName =
            item.listerProduct.condition > 0
              ? this.conditionEnumList[item.listerProduct.condition - 1].name
              : '-';
          item.listerProduct.listingStatusName =
            item.listerProduct.listingStatus > 0
              ? this.listingStatusEnumList[item.listerProduct.listingStatus - 1]
                .name
              : '-';
          item.internalStatusName =
            item.internalStatus > 0
              ? this.internalStausEnumList[item.internalStatus - 1].name
              : '-';
          item.dimensionUnitName =
            item.dimensionUnit > 0
              ? this.dimensionUnitEnumList[item.dimensionUnit - 1].name
              : '-';
          item.weightUnitName =
            item.weightUnit > 0
              ? this.weightUnitEnumList[item.weightUnit - 1].name
              : '-';
        });

        this.paginatedProductsList = data;
        this.totalRecords = data.totalCount;
        this.totalPages = Math.ceil(this.totalRecords / this.itemsPerPage);
        this.pagesToShow = this.paginationUtils.getDisplayedPages(
          this.currentPage,
          this.totalRecords,
          this.itemsPerPage
        );
      });
  }

  onSearchKeyUp(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchSubject.next(value);
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.getProductsList();
  }

  onItemsPerPageChange(size: number) {
    this.itemsPerPage = size;
    this.currentPage = 1;
    this.getProductsList();
  }

  handleTriggerFromChild() {
    this.getProductsList();
  }

  sendProductIdForAddUpdate(productId: number) {
    if (this.productAddEdit) {
      this.productAddEdit.getProductByIdAsync(productId);
    }
  }

  getProductDetails(IMSproductRequest: IMSproductResponse) {
    if (this.productDetails) {
      this.productDetails.getProduct({ ...IMSproductRequest });
    }
  }

  openAiProductCreator() {
    this.showAiDialog = true;
  }

  closeAiDialog() {
    this.showAiDialog = false;
  }

  //=========================================================== sort logic ===========================================================
  changeSorting(field: string) {
    if (this.sortField === field) {
      // Toggle direction
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    this.getProductsList();
  }

  retryAiProcessing(productId: number) {
    this.productService.retryAiProcessing(productId).subscribe({
      next: (response) => {
        this.toaster.success("Product is Requested for Re- Ai Processing")
        this.getProductsList();
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }
  retryListing(productId: number) {
    this.productService.retryListing(productId).subscribe({
      next: (response) => {
        this.toaster.success("Product is Requested for Re-Listing")
        this.getProductsList();
      },
      error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }
}
